import { Radio } from "@components";
import { Numbers } from "@utils/Numbers";
import classNames from "classnames/bind";
import { useState } from "react";
import { VariableSchema } from "../VariableSchema";
import { Titles } from "../variables/Titles";
import { TypesVariable } from "../variables/VariableTypes";

import { Oraculo } from "@core/oraculo";
import OcdpAPI from "@providers/OcdpAPI";
import styles from "@styles/containers/oraculo/audiences.module.scss";
import ROLES from "data/Roles";
import { UserStorage } from "shared/tools/UserStorage";
import OracleDrawer from "../OracleDrawer";
import { Variables as FilterVariables } from '../../cdp/filters/Variables'

const css = classNames.bind(styles);

const TYPE_LABEL = {
  STATIC: "Estático",
  DYNAMIC: "Dinâmico",
};

export const AudienceTableItem = ({
  audience,
  segments,
  onSelect,
  onSaved,
  onDeleted,
  selectedAudience,
  allowEdit,
  viewAudience,
}) => {
  const selectedAudienceId =
    typeof selectedAudience === "number"
      ? selectedAudience
      : selectedAudience?.id;

  const is_selected = selectedAudienceId === audience.id;
  const [collapsed, setCollapsed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [in_progress, setInProgress] = useState(false);

  const toggleAudience = () => onSelect(!is_selected ? audience : null);

  const filters = audience.version == 'V1' ? Object.entries(audience?.filters || {}).map(([k, v]) => ({ key: k, value: v })) : audience?.filters || [];

  const deleteFn = async (audience) => {
    const ocdp = new OcdpAPI();
    const response = ocdp.delete_audience(audience.id);

    if (!response) {
      return alert({ text: "Ocorreu um erro ao excluir o público" });
    }

    if (onDeleted) {
      onDeleted(audience);
    }
    setVisible(false);
  };

  const save_fn = async ({ boxes, links, schema }) => {
    try {
      setInProgress(true);

      const ocdp = new OcdpAPI();
      const response = await ocdp.upsert_audience(audience.id, {
        name: audience.name,
        type: audience.type,
        filters: audience.filters,
        boxes: boxes,
        links: links,
        schema: schema,
      });

      if (!response?.execution_id) {
        throw new Error("Ocorreu um erro durante o salvamento do público");
      }

      const exec = await Oraculo.waitQueryExecution(response.execution_id);
      if (exec?.status == "ERROR") {
        throw new Error("Ocorreu um erro durante o salvamento do público");
      }

      onSaved && onSaved();
      setVisible(false);
    } catch (err) {
      alert({ text: err.message });
    }
    setInProgress(false);
  };

  let userRole = UserStorage.getLoggedUser()?.role;

  if (audience.name?.trimStart().toLowerCase().startsWith('[bloqueado] ') && userRole !== ROLES.SUPPORT) return

  return (
    <>
      <tr className={css({ audience: true, table: true })}>
        <td
          onClick={toggleAudience}
          style={{ borderBottomLeftRadius: collapsed ? 0 : 10 }}
        >
          <div className="d-flex align-center">
            <div className={styles.icon}>
              {is_selected ? (
                <Radio value={is_selected} checked={is_selected} readOnly />
              ) : (
                <i className="fas fa-users" />
              )}
            </div>
            <p>{audience.name}</p>
          </div>
        </td>
        <Cell
          onClick={toggleAudience}
          data={`${Numbers.formatInt(audience.qtd_customers || 0)} pessoas`}
        />
        <Cell onClick={toggleAudience} data={TYPE_LABEL[audience.type]} />
        <Cell onClick={toggleAudience} data={audience.qtv_variables} />
        <Cell onClick={toggleAudience}>
          <p className={css({ inUse: false })}>{"---"}</p>
        </Cell>
        <td style={{ borderBottomRightRadius: collapsed ? 0 : 10 }}>
          <div className={styles.actions}>
            {allowEdit && (
              <>
                <i className="fal fa-pen" onClick={() => setVisible(true)} />
                <div className={styles.line} />
              </>
            )}
            {viewAudience && (
              <>
                <i
                  className="fal fa-eye"
                  onClick={() => window.open(`/base-de-dados/${audience.id}`)}
                />
                <div className={styles.line} />
              </>
            )}
            <i
              className="fas fa-chevron-down"
              style={{ transform: collapsed ? "rotate(180deg)" : "rotate(0)" }}
              onClick={() => setCollapsed(!collapsed)}
            />
          </div>
        </td>
      </tr>
      <tr
        className={css({ audience: true, filters: true })}
        style={{ display: collapsed ? "" : "none" }}
      >
        <td colSpan="6">
          <div className={styles.audienceFilters}>
            <div className={styles.filters}>
              {audience?.schema?.map((v) => (
                <VariableChip
                  variable={v}
                  segments={segments}
                  version={audience.version}
                />
              ))}
              {filters?.map((f) => (
                <FilterChip filter={f} version={audience.version} />
              ))}
            </div>
          </div>
        </td>
      </tr>

      <tr className={styles.spacer}>
        <td colSpan="6"></td>
      </tr>

      <OracleDrawer
        visible={visible}
        onQuery={({ boxes, links, schema }) => {
          save_fn({ boxes, links, schema });
        }}
        onDelete={() => deleteFn(audience)}
        boxes={audience.boxes}
        links={audience.links}
        onClose={() => setVisible(false)}
        audience={audience}
        in_progress={in_progress}
      />
    </>
  );
};

const Cell = ({ children, data, onClick }) => {
  return (
    <td onClick={onClick}>
      <div>{children ? children : <p>{data}</p>}</div>
    </td>
  );
};

const VariableChip = ({ version, segments, variable }) => {

  const getVariableV1 = () => {
    const count = VariableSchema.countValidClauses(variable);
    const title = Titles[TypesVariable[variable.variable]];
    return { count, title };
  }

  const getVariableV2 = () => {
    const count = variable?.values?.length || 0;
    const title = segments?.[variable.key]?.name || "";
    return { count, title };
  }

  const { count, title } = version == 'V1' ? getVariableV1() : getVariableV2();

  return (
    <div className={styles.filter}>
      <p className={styles.name}>{title}</p>
      <span className={styles.qty}>{count}</span>
    </div>
  );
};

const FilterChip = ({ version, filter }) => {

  const getFilterV1 = () => {
    const key = filter?.key;
    const content = filter?.value;

    const title = Titles[key];
    const count = content?.value?.length || content?.length || "--";

    return { count, title };
  }

  const getFilterV2 = () => {
    const title = FilterVariables.find(v => v.type === filter?.alias)?.label || "";
    const count = filter?.values?.length || "--";
    return { count, title };
  }

  const { title, count } = version == 'V1' ? getFilterV1() : getFilterV2();

  return (
    <div className={styles.filter}>
      <p className={styles.name}>{title}</p>
      <span className={styles.qty}>{count}</span>
    </div>
  );
};
